// The QM quote number ASSIGNED to the project, as it is read from the quote
// documents in hand (quoteData.meta.qmQuoteNo), for binding into the saved
// costing receipt.
//
// A quote that SAYS its number is authoritative about it, including when it
// says there is none: `meta.qmQuoteNo` present as null or '' is an operator's
// clear (or an unassigned draft) and must not be answered with an older number
// from another copy of the same quote. A quote that does not mention the number
// at all (no `meta`, or no `qmQuoteNo` property: a partial override) defers to
// the next copy. This reads the quotes it is given and nothing else; which
// quotes are given, and that they belong to the project being costed, is the
// caller's guard.

export type AssignedQmQuoteNo = string | null | undefined;

type QuoteLike = { meta?: unknown } | null | undefined;

/**
 * @param quotes quote documents in priority order (for example a guarded
 *   override first, then the live quote). Entries that are not objects are
 *   ignored.
 * @returns the number as text; null when a quote is loaded and holds none (or
 *   explicitly cleared it); undefined when no quote is available to ask.
 */
export function resolveAssignedQmQuoteNo(quotes: readonly QuoteLike[]): AssignedQmQuoteNo {
    const loaded = quotes.filter((quote): quote is { meta?: unknown } => !!quote && typeof quote === 'object');
    if (loaded.length === 0) return undefined;
    for (const quote of loaded) {
        const meta = quote.meta;
        if (!meta || typeof meta !== 'object' || !Object.prototype.hasOwnProperty.call(meta, 'qmQuoteNo')) continue;
        const raw = (meta as { qmQuoteNo?: unknown }).qmQuoteNo;
        const value = typeof raw === 'string' ? raw.trim() : '';
        return value || null;
    }
    return null;
}
